import {useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {fetchIdentificationInfo} from '@rocket-ui/lk-api';
import {
	extractRegistrationBackendFlags,
	readRegistrationResume,
	readSavedSnilsDigits,
	REGISTRATION_EDIT_PARAM,
} from 'features/registration';
import type {RegistrationWizardState} from './useRegistrationWizardState';

const fillEmpty = <T extends Record<string, unknown>>(current: T, prefill: Partial<T>): T => {
	const next = {...current};

	for (const key of Object.keys(prefill) as (keyof T)[]) {
		const value = prefill[key];

		if ((next[key] === '' || next[key] == null) && value) {
			next[key] = value as T[keyof T];
		}
	}

	return next;
};

/**
 * Перезагрузка `/registration`: по GET /identification/info открываем последний незаполненный шаг.
 * Вход с профиля (`?edit=` или `/profile/statuses`) не трогаем.
 */
export const useRegistrationProgressRestore = (state: RegistrationWizardState): boolean => {
	const [searchParams] = useSearchParams();
	const skipRestore = useRef(
		state.openedFromProfileAtStatuses || searchParams.has(REGISTRATION_EDIT_PARAM),
	).current;
	const [ready, setReady] = useState(skipRestore);
	const {
		preserveDocumentsFlowKeyRef,
		setBackendFlags,
		setDocumentsUpload,
		setForeignDocumentDetails,
		setHasSnilsSaved,
		setIdentificationDocumentPhotos,
		setInnStep,
		setPassportDetails,
		setPersonalInfo,
		setSnilsStep,
		setStep,
		setTaxIdentifierStep,
	} = state;

	useEffect(() => {
		if (skipRestore) {
			return;
		}

		let cancelled = false;

		void fetchIdentificationInfo()
			.then(({data}) => {
				if (cancelled) {
					return;
				}

				const resume = readRegistrationResume(data);

				preserveDocumentsFlowKeyRef.current = resume.documentsFlowKey;
				setBackendFlags(extractRegistrationBackendFlags(data));

				const savedSnils = readSavedSnilsDigits(data?.info);

				if (savedSnils) {
					setHasSnilsSaved(true);
					setSnilsStep({snils: savedSnils});
				}

				setPersonalInfo((current) => ({...current, ...resume.personalInfo}));
				setPassportDetails((current) => fillEmpty(current, resume.passportDetails));
				setForeignDocumentDetails((current) => ({
					...fillEmpty(current, resume.foreignDocumentDetails),
					...(resume.foreignDocumentDetails.documentType
						? {documentType: resume.foreignDocumentDetails.documentType}
						: {}),
				}));
				setIdentificationDocumentPhotos(resume.photos);
				setDocumentsUpload(resume.documentsUpload);

				if (resume.inn) {
					setInnStep((current) => (current.inn ? current : {inn: resume.inn}));
				}

				if (resume.taxIdentifier) {
					setTaxIdentifierStep((current) =>
						current.taxIdentifier ? current : {taxIdentifier: resume.taxIdentifier},
					);
				}

				setStep(resume.step);
			})
			.catch(() => {})
			.finally(() => {
				if (!cancelled) {
					setReady(true);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [
		preserveDocumentsFlowKeyRef,
		setBackendFlags,
		setDocumentsUpload,
		setForeignDocumentDetails,
		setHasSnilsSaved,
		setIdentificationDocumentPhotos,
		setInnStep,
		setPassportDetails,
		setPersonalInfo,
		setSnilsStep,
		setStep,
		setTaxIdentifierStep,
		skipRestore,
	]);

	return ready;
};
